import { isOfflineModeEnabled, getOfflineUserId, getOfflineOrganizationId, onOfflineModeDisabled } from './featureFlag';
import { notifyOfflineUnavailable } from './offlineCapabilities';
import { connectivityMonitor } from './connectivityMonitor';
import { cacheWriter } from './cacheWriter';
import { cacheReader } from './cacheReader';
import { syncQueueService } from './syncQueue';
import { generateTempId, isTempId, resolveTempIdsInText } from './tempIdManager';
import { findTempIds } from './tempIdText';
import offlineDb from './db';
import { isNetworkError } from '../auth';

// Request timeouts, applied ONLY inside the offline-enabled branch and only
// when the caller did not bring its own AbortSignal. Without one, a hung
// backend socket would hang the request forever and the cache fallback would
// never fire. Mutations get longer: aborting a write that actually landed
// server-side creates a duplicate-replay problem, so we err patient.
const GET_TIMEOUT_MS = 20000;
const MUTATION_TIMEOUT_MS = 30000;

// Replay of queued writes after the backend returns. Nothing needs these
// writes to land quickly, and after an outage every client reconnects within
// seconds of each other, so each client waits a random stretch of CONTINUOUS
// healthy backend before replaying. A wobble during the wait cancels it (the
// next successful probe draws a new delay), so a short up-window in a crash
// loop never starts a replay.
const REPLAY_DELAY_MS = [60 * 1000, 300 * 1000];
const REPLAY_WAIT_SLICE_MS = 5000;

// Appointment fields that must not be changed offline (agreed rule:
// status transitions sync back; creation/rescheduling/reassignment do not).
const SCHEDULING_FIELDS = [
  'appointment_date', 'start_time', 'end_time', 'start_timestamp', 'end_timestamp',
  'duration_minutes', 'provider_id', 'patient_id', 'location_id', 'appointment_type', 'timezone',
];

let _originalRequest = null;

export function getOriginalRequest() {
  return _originalRequest;
}

// True when a failure points at the backend being unreachable/unhealthy
// (network error, our own timeout, or a 5xx) rather than a request-level
// rejection like a 400/403/404.
export function isBackendUnavailable(error, ourController = null) {
  return (
    isNetworkError(error) ||
    error?.offline === true ||
    (typeof error?.status === 'number' && error.status >= 500 && error.status <= 599) ||
    error?.name === 'TimeoutError' ||
    (error?.name === 'AbortError' && ourController?.signal?.aborted === true)
  );
}

function withOfflineTimeout(options, ms) {
  if (options.signal) return { options, controller: null, timer: null };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { options: { ...options, signal: controller.signal }, controller, timer };
}

export function installOfflineMiddleware(apiServiceInstance) {
  const originalRequest = apiServiceInstance.request.bind(apiServiceInstance);
  _originalRequest = originalRequest;

  apiServiceInstance.request = async function (endpoint, options = {}, _methodName = null, queueOptions = {}) {
    if (!isOfflineModeEnabled()) {
      return originalRequest(endpoint, options, _methodName, queueOptions);
    }

    const method = (options.method || 'GET').toUpperCase();

    if (method === 'GET') {
      return handleGetRequest(endpoint, options, _methodName, queueOptions, originalRequest);
    }

    return handleMutationRequest(endpoint, options, _methodName, queueOptions, originalRequest);
  };

  // publicRequest: unauthenticated GETs (provider availability, schedule
  // overrides for the calendar grid). Exact-endpoint-key cache.
  const originalPublicRequest = apiServiceInstance.publicRequest.bind(apiServiceInstance);
  apiServiceInstance.publicRequest = async function (endpoint, options = {}) {
    if (!isOfflineModeEnabled() || (options.method || 'GET').toUpperCase() !== 'GET') {
      return originalPublicRequest(endpoint, options);
    }

    if (!connectivityMonitor.isOnline()) {
      const cached = await offlineDb.publicCache.get(endpoint);
      if (cached) return cached.data;
      const err = new Error(`Offline: no cached data for ${endpoint.split('?')[0]}`);
      err.offline = true;
      throw err;
    }

    try {
      const result = await originalPublicRequest(endpoint, options);
      offlineDb.publicCache.put({ key: endpoint, data: result, _cachedAt: Date.now() }).catch(() => {});
      return result;
    } catch (error) {
      if (isBackendUnavailable(error)) {
        connectivityMonitor.reportBackendFailure();
        const cached = await offlineDb.publicCache.get(endpoint);
        if (cached) return cached.data;
      }
      throw error;
    }
  };

  // A client with queued offline writes stays in offline mode (cache reads,
  // queued writes: the tested offline path) until its own queue has been
  // replayed. Going online first would let a newer online save be overwritten
  // by an older queued one, and a re-opened record be refetched over local
  // edits. Clients with nothing queued go online at once.
  connectivityMonitor.setReconnectGate(async (isHealthy) => {
    if (!isOfflineModeEnabled()) return true;
    if ((await syncQueueService.getPendingCount()) === 0) return true;

    const [min, max] = REPLAY_DELAY_MS;
    const waitMs = min + Math.floor(Math.random() * (max - min));
    for (let waited = 0; waited < waitMs; waited += REPLAY_WAIT_SLICE_MS) {
      await new Promise((resolve) => setTimeout(resolve, REPLAY_WAIT_SLICE_MS));
      if (!isOfflineModeEnabled()) return true;
      if (!isHealthy()) return false;
    }

    await syncQueueService.processQueue(originalRequest, { isHealthy });
    return (await syncQueueService.getPendingCount()) === 0;
  });

  connectivityMonitor.initialize();

  // Trigger sync when coming back online
  connectivityMonitor.subscribe((online) => {
    if (online && isOfflineModeEnabled()) {
      syncQueueService.processQueue(originalRequest);
    }
  });

  // Offline mode turned off remotely: nothing drains the queue afterwards,
  // so send whatever is pending now (the probe that found the change just
  // succeeded, so the backend is reachable).
  onOfflineModeDisabled(() => {
    syncQueueService.processQueue(originalRequest);
  });

  // Items can be queued while the monitor still believes we are online (the
  // request failed before any probe flipped the state) — drain shortly after
  // any enqueue that happens while nominally online.
  let enqueueDrainTimer = null;
  syncQueueService.subscribe((event) => {
    if (event !== 'queue:added') return;
    if (!connectivityMonitor.isOnline() || !isOfflineModeEnabled()) return;
    clearTimeout(enqueueDrainTimer);
    enqueueDrainTimer = setTimeout(() => {
      if (connectivityMonitor.isOnline()) {
        syncQueueService.processQueue(originalRequest);
      }
    }, 5000);
  });

  // Boot drain: pending work left from a previous session.
  setTimeout(async () => {
    if (!isOfflineModeEnabled() || !connectivityMonitor.isOnline()) return;
    try {
      const pending = await syncQueueService.getPendingCount();
      if (pending > 0) syncQueueService.processQueue(originalRequest);
    } catch { /* ignore */ }
  }, 3000);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    if (!isOfflineModeEnabled() || !connectivityMonitor.isOnline()) return;
    syncQueueService.getPendingCount().then((pending) => {
      if (pending > 0) syncQueueService.processQueue(originalRequest);
    }).catch(() => {});
  });
}

async function handleGetRequest(endpoint, options, _methodName, queueOptions, originalRequest) {
  if (!connectivityMonitor.isOnline()) {
    const cached = await cacheReader.resolveFromCache(endpoint);
    if (cached !== null && cached !== undefined) {
      return cached;
    }
    const err = new Error(`Offline: no cached data for ${endpoint.split('?')[0]}`);
    err.offline = true;
    throw err;
  }

  const { options: timedOptions, controller, timer } = withOfflineTimeout(options, GET_TIMEOUT_MS);
  try {
    const result = await originalRequest(endpoint, timedOptions, _methodName, queueOptions);
    cacheWriter.cacheFromEndpoint(endpoint, result).catch(() => {});
    return result;
  } catch (error) {
    if (isBackendUnavailable(error, controller)) {
      connectivityMonitor.reportBackendFailure();
      const cached = await cacheReader.resolveFromCache(endpoint);
      if (cached !== null && cached !== undefined) {
        return cached;
      }
    }
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function resolveEndpointTempIds(endpoint) {
  if (!endpoint.includes('temp_')) return endpoint;
  const mappings = await offlineDb.idMappings.toArray();
  let resolved = endpoint;
  for (const m of mappings) {
    if (resolved.includes(m.tempId)) {
      resolved = resolved.replace(m.tempId, m.serverId);
    }
  }
  return resolved;
}

async function handleMutationRequest(endpoint, options, _methodName, queueOptions, originalRequest) {
  // Known offline: don't burn 3+ seconds of retries per write — divert now.
  if (!connectivityMonitor.isOnline()) {
    return handleOfflineWrite(endpoint, options, _methodName);
  }

  const resolvedEndpoint = await resolveEndpointTempIds(endpoint);

  // Placeholder ids (temp_<entity>_<uuid>) in the body or URL: rewrite the ones
  // whose create has synced; if any is still unmapped, this write depends on a
  // queued create and must join the queue behind it instead of going out live
  // (a live merge with a placeholder note_id strands billing rows on the
  // encounter, 2026-10-08).
  let liveOptions = options;
  if (typeof options.body === 'string' && options.body.includes('temp_')) {
    const { text, unresolved } = await resolveTempIdsInText(options.body);
    if (unresolved.length > 0) {
      return handleOfflineWrite(endpoint, options, _methodName);
    }
    liveOptions = { ...options, body: text };
  }
  if (findTempIds(resolvedEndpoint).length > 0) {
    return handleOfflineWrite(endpoint, options, _methodName);
  }

  const { options: timedOptions, controller, timer } = withOfflineTimeout(liveOptions, MUTATION_TIMEOUT_MS);
  try {
    const result = await originalRequest(resolvedEndpoint, timedOptions, _methodName, queueOptions);
    if (result && (options.method === 'POST' || options.method === 'PATCH')) {
      cacheWriter.cacheFromEndpoint(resolvedEndpoint, result).catch(() => {});
    }
    return result;
  } catch (error) {
    if (isBackendUnavailable(error, controller)) {
      connectivityMonitor.reportBackendFailure();
      return handleOfflineWrite(endpoint, options, _methodName);
    }
    throw error;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function handleOfflineWrite(endpoint, options, methodName) {
  const path = endpoint.split('?')[0];
  const payload = options.body ? JSON.parse(options.body) : {};

  // POST /visit — create visit atomically
  if (options.method === 'POST' && path === '/visit') {
    return handleOfflineCreateVisit(endpoint, payload, methodName);
  }

  // PATCH /clinical_note/{id} — update note
  const noteUpdateMatch = path.match(/^\/clinical_note\/([^/]+)$/);
  if (options.method === 'PATCH' && noteUpdateMatch) {
    return handleOfflineUpdateNote(noteUpdateMatch[1], endpoint, payload, methodName);
  }

  // PATCH /clinical_note_sign/{id} — sign note
  const noteSignMatch = path.match(/^\/clinical_note_sign\/([^/]+)$/);
  if (options.method === 'PATCH' && noteSignMatch) {
    return handleOfflineSignNote(noteSignMatch[1], endpoint, payload, methodName);
  }

  // PATCH /encounter/{id} — update encounter
  const encounterMatch = path.match(/^\/encounter\/([^/]+)$/);
  if (options.method === 'PATCH' && encounterMatch) {
    return handleOfflineUpdateEncounter(encounterMatch[1], endpoint, payload, methodName);
  }

  // PATCH /appointment/{id} — status transitions only (field-based rule)
  const appointmentMatch = path.match(/^\/appointment\/([^/]+)$/);
  if (options.method === 'PATCH' && appointmentMatch) {
    return handleOfflineUpdateAppointment(appointmentMatch[1], endpoint, payload, methodName);
  }

  // Cash copay chain: invoice create → record payment → appointment PATCH.
  if (options.method === 'POST' && path === '/billing/invoices/create') {
    return handleOfflineCreateInvoice(endpoint, payload, methodName);
  }
  if (options.method === 'POST' && path === '/billing/invoices/record-payment') {
    return handleOfflineRecordPayment(endpoint, payload, methodName);
  }

  // PATCH /encounter-merge-billing-codes/{id} — idempotent server-side merge,
  // fully derivable from the (already queued) note PATCH payload.
  const mergeMatch = path.match(/^\/encounter-merge-billing-codes\/([^/]+)$/);
  if (options.method === 'PATCH' && mergeMatch) {
    return handleOfflineMergeBillingCodes(mergeMatch[1], endpoint, payload, methodName);
  }

  // Flow sheet entries — generic queueing with temp ids; the note editor's
  // projection logic runs unchanged against the cache.
  if (options.method === 'POST' && path === '/flow-sheet-entry') {
    return handleOfflineCreateFlowSheetEntry(endpoint, payload, methodName);
  }
  const flowEntryMatch = path.match(/^\/flow-sheet-entry\/([^/]+)$/);
  if (options.method === 'PATCH' && flowEntryMatch) {
    return handleOfflineUpdateFlowSheetEntry(flowEntryMatch[1], endpoint, payload, methodName);
  }
  if (options.method === 'DELETE' && flowEntryMatch) {
    return handleOfflineDeleteFlowSheetEntry(flowEntryMatch[1], endpoint, methodName);
  }

  // Audit log entries: queue with their true event_date instead of dropping.
  if (path === '/erx_mobile_audit_logs' && options.method === 'POST') {
    await syncQueueService.enqueue({
      operationType: 'audit',
      entityType: 'auditLog',
      entityId: null,
      method: 'POST',
      endpoint,
      payload,
      methodName,
    });
    return { success: true, offline: true, queued: true };
  }

  // For unhandled mutations offline, throw a clear error and surface the
  // standard notification.
  const err = new Error(`Cannot perform this action offline: ${options.method} ${path}`);
  err.offline = true;
  err.offlineUnavailable = true;
  notifyOfflineUnavailable();
  throw err;
}

// --- Cash copay (invoice create → record payment) -------------------------

async function handleOfflineCreateInvoice(endpoint, payload, methodName) {
  const tempInvoiceId = generateTempId('invoice');
  const lineItems = payload.line_items || [];
  const subtotal = lineItems.reduce((sum, li) => sum + (parseFloat(li.total) || 0), 0);

  const invoice = {
    id: tempInvoiceId,
    created_at: Date.now(),
    patient_id: payload.patient_id,
    appointment_id: payload.appointment_id || null,
    invoice_type: payload.invoice_type || 'manual',
    // The server allocates invoice_number; unknown until sync.
    invoice_number: null,
    line_items: lineItems,
    subtotal,
    total_amount: subtotal,
    amount_paid: 0,
    balance_due: subtotal,
    status: 'pending',
    manual_payments: [],
    _offlineCreated: true,
  };

  await offlineDb.invoices.put(invoice);

  await syncQueueService.enqueue({
    operationType: 'create',
    entityType: 'invoice',
    entityId: null,
    tempId: tempInvoiceId,
    method: 'POST',
    endpoint,
    payload,
    methodName,
  });

  return invoice;
}

async function handleOfflineRecordPayment(endpoint, payload, methodName) {
  // Idempotency: the server skips a payment whose client_reference it has
  // already recorded, so a replay after a lost response cannot double-post.
  const clientReference = crypto.randomUUID();
  const queuedPayload = { ...payload, client_reference: clientReference };

  await syncQueueService.enqueue({
    operationType: 'payment',
    entityType: 'invoicePayment',
    entityId: null,
    method: 'POST',
    endpoint,
    payload: queuedPayload,
    methodName,
    dependsOn: isTempId(payload.invoice_id) ? payload.invoice_id : null,
  });

  // Update the local invoice echo so the UI reflects the collected payment.
  const invoice = await offlineDb.invoices.get(payload.invoice_id);
  if (invoice) {
    const amount = parseFloat(payload.amount) || 0;
    const amountPaid = (invoice.amount_paid || 0) + amount;
    const balanceDue = Math.max(0, (invoice.total_amount || 0) - amountPaid);
    const updated = {
      ...invoice,
      amount_paid: amountPaid,
      balance_due: balanceDue,
      status: balanceDue <= 0 ? 'paid' : 'partial',
      paid_at: balanceDue <= 0 ? Date.now() : invoice.paid_at || null,
      manual_payments: [...(invoice.manual_payments || []), {
        amount,
        method: payload.method,
        reference: payload.reference || null,
        notes: payload.notes || null,
        client_reference: clientReference,
        recorded_at: Date.now(),
        _offlineCreated: true,
      }],
    };
    await offlineDb.invoices.put(updated);
    return updated;
  }

  return { success: true, offline: true, queued: true };
}

// --- Encounter billing-code merge ------------------------------------------

async function handleOfflineMergeBillingCodes(encounterId, endpoint, payload, methodName) {
  // entityType must NOT be 'encounter': the queue coalesces updates by
  // (entityId, entityType) and this body targets a different endpoint than
  // PATCH /encounter/{id}.
  await syncQueueService.enqueue({
    operationType: 'update',
    entityType: 'encounterBillingCodes',
    entityId: encounterId,
    method: 'PATCH',
    endpoint,
    payload,
    methodName,
    dependsOn: isTempId(encounterId)
      ? encounterId
      : (isTempId(payload.note_id) ? payload.note_id : null),
  });

  return { success: true, offline: true, queued: true };
}

// --- Flow sheet entries -----------------------------------------------------

function firstTempIdIn(...ids) {
  for (const id of ids) {
    if (isTempId(id)) return id;
  }
  return null;
}

async function handleOfflineCreateFlowSheetEntry(endpoint, payload, methodName) {
  const tempId = generateTempId('flowSheetEntry');

  const row = {
    id: tempId,
    ...payload,
    is_active: payload.is_active !== false,
    provider_id: getOfflineUserId(),
    organization_id: getOfflineOrganizationId(),
    recorded_at: Date.now(),
    created_at: Date.now(),
    _offlineCreated: true,
  };
  await offlineDb.flowSheetEntries.put(row);

  await syncQueueService.enqueue({
    operationType: 'create',
    entityType: 'flowSheetEntry',
    entityId: null,
    tempId,
    method: 'POST',
    endpoint,
    // recorded_at carries the true observation time; the POST endpoint
    // honors it when present (offline mode Xano change).
    payload: { ...payload, recorded_at: new Date().toISOString() },
    methodName,
    dependsOn: firstTempIdIn(payload.clinical_note_id, payload.encounter_id),
  });

  return row;
}

async function handleOfflineUpdateFlowSheetEntry(entryId, endpoint, payload, methodName) {
  const existing = await offlineDb.flowSheetEntries.get(entryId);
  if (existing) {
    await offlineDb.flowSheetEntries.put({ ...existing, ...payload, _localTouchedAt: Date.now() });
  }

  // A PATCH against a temp id folds into its unsent create (one replay,
  // correct final state) instead of a dependent update item.
  if (isTempId(entryId)) {
    const merged = await syncQueueService.mergeIntoPendingCreate(entryId, payload);
    if (merged) {
      return { ...existing, ...payload, id: entryId };
    }
  }

  await syncQueueService.enqueue({
    operationType: 'update',
    entityType: 'flowSheetEntry',
    entityId: entryId,
    method: 'PATCH',
    endpoint,
    payload,
    methodName,
    dependsOn: isTempId(entryId) ? entryId : null,
  });

  return { ...existing, ...payload, id: entryId };
}

async function handleOfflineDeleteFlowSheetEntry(entryId, endpoint, methodName) {
  await offlineDb.flowSheetEntries.delete(entryId);

  if (isTempId(entryId)) {
    // Never sent: cancel the pending create (and any merged updates).
    await syncQueueService.removePendingForEntity(entryId);
    return null;
  }

  // Drop pending updates for the row, then queue the delete (server DELETE
  // is replay-safe: no existence check).
  await syncQueueService.removePendingForEntity(entryId);
  await syncQueueService.enqueue({
    operationType: 'delete',
    entityType: 'flowSheetEntry',
    entityId: entryId,
    method: 'DELETE',
    endpoint,
    payload: {},
    methodName,
  });

  return null;
}

async function handleOfflineCreateVisit(endpoint, payload, methodName) {
  const tempEncounterId = generateTempId('encounter');
  const tempNoteId = generateTempId('clinicalNote');

  let provider = null;
  if (payload.provider_id) {
    provider = await offlineDb.organizationUsers.get(payload.provider_id);
  }
  const providerAddon = provider ? {
    id: provider.id,
    first_name: provider.first_name,
    last_name: provider.last_name,
    provider_designation: provider.provider_designation,
    npi_number: provider.npi_number,
    license_number: provider.license_number,
  } : null;

  const encounter = {
    id: tempEncounterId,
    created_at: Date.now(),
    updated_at: Date.now(),
    patient_id: payload.patient_id,
    provider_id: payload.provider_id,
    organization_id: payload.organization_id,
    encounter_date: payload.encounter_date,
    encounter_type: payload.encounter_type || 'follow_up',
    visit_type: payload.visit_type || 'office',
    clinical_specialty: payload.clinical_specialty,
    case_id: payload.case_id,
    appointment_id: payload.appointment_id,
    clinical_note_id: tempNoteId,
    status: 'in_progress',
    documentation_status: 'in_progress',
    body_regions: payload.body_regions || [],
    functional_areas: payload.functional_areas || [],
    billing_codes: { icd10_codes: payload.icd10_codes || [], cpt_codes: [] },
    provider: providerAddon,
    _offlineCreated: true,
  };

  const clinicalNote = {
    id: tempNoteId,
    created_at: Date.now(),
    updated_at: Date.now(),
    encounter_id: tempEncounterId,
    patient_id: payload.patient_id,
    provider_id: payload.provider_id,
    case_id: payload.case_id,
    note_type: payload.note_type || 'soap',
    template_id: payload.template_id || null,
    signed: false,
    subjective: null,
    objective: null,
    assessment: null,
    plan: null,
    sections_data: payload.sections_data || null,
    icd10_codes: payload.icd10_codes || [],
    cpt_codes: [],
    body_regions: payload.body_regions || [],
    functional_areas: payload.functional_areas || [],
    contributors: [],
    provider: providerAddon,
    _offlineCreated: true,
  };

  // Store locally
  await offlineDb.encounters.put(encounter);
  await offlineDb.clinicalNotes.put(clinicalNote);

  // Queue for sync
  await syncQueueService.enqueue({
    operationType: 'create',
    entityType: 'visit',
    entityId: null,
    tempId: tempEncounterId,
    method: 'POST',
    endpoint,
    payload: { ...payload, _tempEncounterId: tempEncounterId, _tempNoteId: tempNoteId },
    methodName,
  });

  return { encounter, clinical_note: clinicalNote };
}

async function handleOfflineUpdateNote(noteId, endpoint, payload, methodName) {
  // Update local cache
  const existing = await offlineDb.clinicalNotes.get(noteId);
  if (existing) {
    await offlineDb.clinicalNotes.update(noteId, { ...payload, updated_at: Date.now(), _localTouchedAt: Date.now() });
  }

  // Queue (with coalescence)
  await syncQueueService.enqueue({
    operationType: 'update',
    entityType: 'clinicalNote',
    entityId: noteId,
    method: 'PATCH',
    endpoint,
    payload,
    methodName,
    dependsOn: isTempId(noteId) ? noteId : null,
  });

  return { ...existing, ...payload, id: noteId, updated_at: Date.now() };
}

async function handleOfflineSignNote(noteId, endpoint, payload, methodName) {
  if (payload.discharge_case === true) {
    throw new Error('Reconnect before signing with Discharge case checked. Your draft can still be saved offline.');
  }
  const existing = await offlineDb.clinicalNotes.get(noteId);
  const signedNote = {
    ...existing,
    signed: true,
    signed_at: Date.now(),
    signed_by: payload.signature,
    _signedOffline: true,
  };

  await offlineDb.clinicalNotes.put(signedNote);

  await syncQueueService.enqueue({
    operationType: 'sign',
    entityType: 'clinicalNote',
    entityId: noteId,
    method: 'PATCH',
    endpoint,
    // signed_at carries the true signing moment; the sign endpoint honors it
    // when present (offline mode Xano change) instead of stamping sync time.
    payload: { ...payload, signed_at: new Date().toISOString() },
    methodName,
    dependsOn: isTempId(noteId) ? noteId : null,
  });

  return signedNote;
}

async function handleOfflineUpdateEncounter(encounterId, endpoint, payload, methodName) {
  const existing = await offlineDb.encounters.get(encounterId);
  if (existing) {
    await offlineDb.encounters.update(encounterId, { ...payload, updated_at: Date.now(), _localTouchedAt: Date.now() });
  }

  await syncQueueService.enqueue({
    operationType: 'update',
    entityType: 'encounter',
    entityId: encounterId,
    method: 'PATCH',
    endpoint,
    payload,
    methodName,
    dependsOn: isTempId(encounterId) ? encounterId : null,
  });

  return { ...existing, ...payload, id: encounterId, updated_at: Date.now() };
}

async function handleOfflineUpdateAppointment(appointmentId, endpoint, payload, methodName) {
  // Field-based rule: status transitions (check-in, complete, no-show,
  // cancel, undo) and copay recording replay on reconnect; date/time/
  // provider/patient/type/location changes and archiving do not.
  if (SCHEDULING_FIELDS.some((k) => k in payload) || payload.status === 'deleted') {
    const err = new Error(
      'Rescheduling, reassigning, and archiving are not available offline. Reconnect to change date, time, provider, patient, type, or location.'
    );
    err.offline = true;
    err.offlineBlocked = true;
    throw err;
  }

  const existing = await offlineDb.appointments.get(appointmentId);
  if (existing) {
    await offlineDb.appointments.update(appointmentId, { ...payload, updated_at: Date.now(), _localTouchedAt: Date.now() });
  }

  await syncQueueService.enqueue({
    operationType: 'update',
    entityType: 'appointment',
    entityId: appointmentId,
    method: 'PATCH',
    endpoint,
    payload,
    methodName,
  });

  return { ...existing, ...payload, id: appointmentId, updated_at: Date.now() };
}
